/**
 * React Query hooks for connector enrollment status.
 *
 * The platform registers a single Multi-Tenant Microsoft App and a single
 * GitHub App at the DR level (per-provider K8s Secrets: `connector-{provider}-credentials`).
 * This hook exposes the enrollment status per tenant:
 *   - Microsoft: "available" | "not_configured"
 *   - GitHub:    "installed" | "not_installed" + per-tenant installations
 *
 * Path: /settings/integrations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { getErrorMessage } from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

// =============================================================================
// Types
// =============================================================================

export interface GitHubInstallation {
  id: string;
  tenant_id: string;
  installation_id: number;
  account_login: string;
  account_type: string;
  status: string;
  permissions: Record<string, string> | null;
  installed_by: string | null;
  installed_at: string;
  updated_at: string;
}

export interface EnrollmentStatus {
  microsoft: 'available' | 'not_configured';
  microsoft_consent_url: string | null;
  github: 'installed' | 'not_installed';
  github_install_url: string | null;
  github_installations: GitHubInstallation[];
}

export interface GitHubInstallationCreate {
  installation_id: number;
  account_login: string;
  account_type: 'Organization' | 'User';
  permissions?: Record<string, string>;
}

// =============================================================================
// Query Keys
// =============================================================================

export const enrollmentKeys = {
  all: (tenantId: string | undefined) => ['enrollment', tenantId] as const,
  status: (tenantId: string | undefined) => [...enrollmentKeys.all(tenantId), 'status'] as const,
};

// =============================================================================
// API Functions
// =============================================================================

async function fetchEnrollmentStatus(): Promise<EnrollmentStatus> {
  const { data } = await apiClient.get<EnrollmentStatus>('/api/v1/datasources/enrollment/status');
  return data;
}

async function createGitHubInstallation(input: GitHubInstallationCreate): Promise<GitHubInstallation> {
  const { data } = await apiClient.post<GitHubInstallation>('/api/v1/datasources/github/installations', input);
  return data;
}

async function deleteGitHubInstallation(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/datasources/github/installations/${id}`);
}

// =============================================================================
// Hooks
// =============================================================================

export function useEnrollmentStatus() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: enrollmentKeys.status(tenantId),
    queryFn: fetchEnrollmentStatus,
    enabled: !!tenantId,
  });
}

export function useCreateGitHubInstallation() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: (input: GitHubInstallationCreate) => createGitHubInstallation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.all(tenantId) });
    },
  });
}

export function useDeleteGitHubInstallation() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: (id: string) => deleteGitHubInstallation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.all(tenantId) });
    },
  });
}

export { getErrorMessage };
